import {
  queryResumableOffset,
  uploadResumable,
  uploadToSignedUrl,
} from "./gcs-transport";
import { cancelUpload, confirmUpload, requestUploadUrl } from "./uploads.api";
import type {
  DirectUploadOptions,
  DirectUploadResult,
  ResumeDirectUploadOptions,
} from "./types";

const createAbortError = (): DOMException =>
  new DOMException("Aborted", "AbortError");

/**
 * High-level direct-upload orchestrator:
 *  1. Ask the backend for an upload URL: a signed URL for files ≤100 MB, or
 *     a resumable session URI for files >100 MB.
 *  2. PUT the bytes directly to GCS: one PUT to the signed URL, or sequential
 *     50 MB chunks to the resumable session.
 *  3. POST to /uploads/{id}/confirm so the backend marks it COMPLETED, unless
 *     `skipConfirm` is set (the caller attaches it next, which confirms it).
 *
 * On any failure between step 1 and step 3 we fire-and-forget a DELETE so
 * the backend record doesn't linger waiting for the 1-hour expiry.
 */
export const directUpload = async (
  file: File,
  options: DirectUploadOptions = {},
): Promise<DirectUploadResult> => {
  if (options.signal?.aborted) throw createAbortError();

  const config = { metadata: options.metadata };
  const meta = await requestUploadUrl(
    {
      filename: options.filename ?? file.name,
      mime_type: file.type || "application/octet-stream",
      size_bytes: file.size,
      form_field_key: options.form_field_key,
      context: options.context,
      context_id: options.context_id,
    },
    config,
  );

  options.onUploadCreated?.(meta.id, {
    uploadSessionUri: meta.upload_session_uri,
  });

  try {
    const transport = {
      onProgress: options.onProgress,
      signal: options.signal,
    };
    if (meta.signed_url) {
      await uploadToSignedUrl(meta.signed_url, file, transport);
    } else if (meta.upload_session_uri) {
      await uploadResumable(meta.upload_session_uri, file, 0, transport);
    } else {
      throw new Error(
        "Upload response missing both signed_url and upload_session_uri",
      );
    }
    if (!options.skipConfirm) await confirmUpload(meta.id, config);
  } catch (err) {
    // Best-effort cleanup; never await or surface its error.
    // Se omite cuando el llamador administra el ciclo de vida: borrar aquí
    // haría imposible reintentar sobre el mismo registro.
    if (options.cancelOnFailure !== false) {
      cancelUpload(meta.id, config).catch(() => {
        /* swallow */
      });
    }
    throw err;
  }

  // Final tick after confirm so the UI never sees 100% before the file is usable.
  options.onProgress?.(100);

  return {
    id: meta.id,
    size: meta.size_bytes,
    mime_type: meta.mime_type,
    filename: meta.original_filename,
  };
};

/**
 * Resumes an upload that already has a resumable session (>100 MB) instead of
 * discarding it and starting over, which is what `directUpload` would do.
 * Only meaningful within the same tab session — it needs the original
 * `File`'s bytes, which don't survive a reload.
 *
 *  1. Asks GCS how much of `options.sessionUri` it durably has.
 *  2. If the session expired, best-effort deletes the dead record and falls
 *     back to a brand-new `directUpload` (new record, new URL) — from the
 *     caller's perspective this is transparent except that
 *     `options.onUploadCreated`, if given, fires again with the new id.
 *  3. Otherwise uploads only the missing remainder, in chunks (or nothing, if
 *     GCS already has it all) and confirms.
 */
export const resumeDirectUpload = async (
  file: File,
  options: ResumeDirectUploadOptions,
): Promise<DirectUploadResult> => {
  if (options.signal?.aborted) throw createAbortError();

  const config = { metadata: options.metadata };
  const offsetResult = await queryResumableOffset(
    options.sessionUri,
    file.size,
    {
      signal: options.signal,
    },
  );

  if (offsetResult.status === "expired") {
    // La sesión ya no sirve: el registro viejo se borra sin condición (no es
    // el fallo genérico que `cancelOnFailure` gobierna) y se arranca de cero.
    cancelUpload(options.id, config).catch(() => {
      /* swallow */
    });
    return directUpload(file, options);
  }

  try {
    if (
      offsetResult.status === "incomplete" &&
      offsetResult.offset < file.size
    ) {
      await uploadResumable(options.sessionUri, file, offsetResult.offset, {
        onProgress: options.onProgress,
        signal: options.signal,
      });
    }
    // "complete": GCS ya tiene todos los bytes, pero el registro sigue en
    // PENDING hasta este confirm (o hasta el adjunto, con `skipConfirm`).
    if (!options.skipConfirm) await confirmUpload(options.id, config);
  } catch (err) {
    if (options.cancelOnFailure !== false) {
      cancelUpload(options.id, config).catch(() => {
        /* swallow */
      });
    }
    throw err;
  }

  options.onProgress?.(100);

  return {
    id: options.id,
    size: file.size,
    mime_type: file.type || "application/octet-stream",
    filename: options.filename ?? file.name,
  };
};

/**
 * Sequentially upload many files, aggregating per-file progress into a single
 * 0–100 number weighted by byte count.
 */
export const directUploadMany = async (
  files: File[],
  options: DirectUploadOptions = {},
): Promise<DirectUploadResult[]> => {
  if (files.length === 0) return [];

  const totalSize = files.reduce((sum, f) => sum + f.size, 0);
  let completedBytes = 0;
  const results: DirectUploadResult[] = [];

  for (const file of files) {
    const fileSize = file.size;

    const result = await directUpload(file, {
      ...options,
      onProgress:
        options.onProgress && totalSize > 0
          ? (pct) => {
              const cumulative = completedBytes + (pct / 100) * fileSize;
              options.onProgress?.(
                Math.min(99, Math.floor((cumulative * 100) / totalSize)),
              );
            }
          : undefined,
    });

    results.push(result);
    completedBytes += fileSize;
  }

  options.onProgress?.(100);
  return results;
};
